import { createStore } from "./createStore";
import api, { tutorApi } from "@/utils/axios-utils";
import { TutorMode } from "@/constants/mode.ts";
import { useBiteStatusStore } from "./useBiteStatusStore";
import { useNotepadStore } from "@/features/notepad/store/useNotepadStore";
import { useNotepadPerSlideStore } from "@/features/notepad/store/useNotepadPerSlideStore";
import { useNotificationStore } from "@/store/useNotificationStore";
import { debugLog, debugWarn } from "@/utils/debugLog";
import {
  createSlideHtmlCacheController,
  createSlideHtmlCacheState,
} from "@/features/tutor/vo-demo/slides/slideHtmlCacheController";
import { createSlideNavigationController } from "@/features/tutor/vo-demo/deck/slideNavigationController";
import {
  fetchSlideHtml,
  prefetchSlideHtmlAssets,
} from "@/features/tutor/utils/slideHtmlLoader";
import {
  getLessonStartupPerfNow,
  recordLessonStartupPerf,
} from "@/features/tutor/utils/lessonStartupPerf";

// tutorApi has no timeout: how long a change_slide may hold navigation locked.
export const SLIDE_CHANGE_LOCK_MAX_MS = 10_000;

/**
 * hasFailedAiSlideChange: the tutor announced a slide change, its change_slide
 * failed, and the backend has not heard. Rides the next TURN_CHANGE
 * (useWebsocketClient.js). Every store reset clears both fields together.
 */
const IDLE_SLIDE_CHANGE_STATE = {
  pendingSlideChangeRequest: null,
  hasFailedAiSlideChange: false,
};

const base = (set, get) => {
  const slideHtmlCacheController = createSlideHtmlCacheController({
    set,
    get,
    fetchSlideHtml,
    prefetchSlideHtmlAssets,
  });
  const slideNavigationController = createSlideNavigationController({
    set,
    get,
    tutorApi,
    biteStatusStore: useBiteStatusStore,
    notepadStore: useNotepadStore,
    notepadPerSlideStore: useNotepadPerSlideStore,
    debugLog,
    debugWarn,
  });

  return {
    currentLessonId: null,
    currentLessonName: "",
    currentSlideIndex: 0,
    /**
     * Has anyone actually told us which slide we are on?
     *
     * `currentSlideIndex` starts at 0 as a placeholder, which is
     * indistinguishable from a genuine "we are on slide 0" — so a reader that
     * runs before session discovery resolves silently acts on the wrong slide.
     * That is not hypothetical: it keyed the notepad's canvas to slide 0 on a
     * mid-lesson refresh, restoring the wrong page's drawing and then treating
     * the correction as a bite-to-bite navigation.
     *
     * Set by `setCurrentSlideIndex`, so every path that resolves an index —
     * discovery, session boot, resume rehydration, assessment advance — marks
     * it without needing to know this exists. Cleared on lesson entry, where
     * the index goes stale again.
     */
    hasResolvedSlideIndex: false,
    slides: [],
    lessons: [],
    currentDeck: null,
    currentUrl: "/slides/reveal-js/demo/index.html",
    slideFragmentMemory: {},
    slideActionCount: 0,
    ...createSlideHtmlCacheState(),
    slideChangeRequestToken: 0,
    ...IDLE_SLIDE_CHANGE_STATE,
    isManualSlideChangeInFlight: false,
    slideResetVersion: 0,
    incrementSlideActionCount: () =>
      set((state) => ({ slideActionCount: state.slideActionCount + 1 })),
    resetSlideActionCount: () => set({ slideActionCount: 0 }),

    // Get slide by slide_index value (slides array is unordered, indexed by position)
    getSlideByIndex: (slideIndex) => {
      const state = get();
      return state.slides.find((s) => s?.slide_index === slideIndex) ?? null;
    },

    // Map of fragment_id -> action_id for StudentInput actions (current slide)
    getStudentInputActionMap: () => {
      const state = get();
      const currentSlide = state.getSlideByIndex(state.currentSlideIndex);
      if (!currentSlide?.slide_data) return {};

      try {
        const slideData = JSON.parse(currentSlide.slide_data);
        const actionMap = {};

        for (const actionWrapper of slideData.actions || []) {
          const action = actionWrapper.action;
          if (
            action?.name === "StudentInput" &&
            action.fragment_ids?.length > 0
          ) {
            // Map each fragment_id to the action's id
            for (const fragmentId of action.fragment_ids) {
              actionMap[fragmentId] = action.id;
            }
          }
        }
        return actionMap;
      } catch {
        return {};
      }
    },

    // Reverse map: action_id -> fragment_id (for looking up reveal order)
    getActionToFragmentMap: () => {
      const state = get();
      const currentSlide = state.getSlideByIndex(state.currentSlideIndex);
      if (!currentSlide?.slide_data) return {};

      try {
        const slideData = JSON.parse(currentSlide.slide_data);
        const reverseMap = {};

        for (const actionWrapper of slideData.actions || []) {
          const action = actionWrapper.action;
          if (
            action?.name === "StudentInput" &&
            action.fragment_ids?.length > 0
          ) {
            // Use the first fragment_id as the reveal index for this action
            reverseMap[action.id] = action.fragment_ids[0];
          }
        }
        return reverseMap;
      } catch {
        return {};
      }
    },

    // Map answer_fragment_index -> [action_ids] for clearing inputs when answers are revealed
    getAnswerFragmentToActionsMap: () => {
      const state = get();
      const currentSlide = state.getSlideByIndex(state.currentSlideIndex);
      if (!currentSlide?.slide_data) return {};

      try {
        const slideData = JSON.parse(currentSlide.slide_data);
        const answerMap = {};

        for (const actionWrapper of slideData.actions || []) {
          const action = actionWrapper.action;
          if (
            action?.name === "StudentInput" &&
            action.answer_fragment_index !== undefined
          ) {
            const answerFragmentIndex = action.answer_fragment_index;
            if (!answerMap[answerFragmentIndex]) {
              answerMap[answerFragmentIndex] = [];
            }
            answerMap[answerFragmentIndex].push(action.id);
          }
        }
        return answerMap;
      } catch {
        return {};
      }
    },

    // Get all action IDs whose answer_fragment_index is at or below the target fragment
    // Used when jump-ahead reveals a fragment to mark ALL preceding inputs as revealed
    getInputsToRevealUpToFragment: (targetFragment) => {
      const state = get();
      const currentSlide = state.getSlideByIndex(state.currentSlideIndex);
      if (!currentSlide?.slide_data) return [];

      try {
        const slideData = JSON.parse(currentSlide.slide_data);
        const actionIdsToReveal = [];

        for (const actionWrapper of slideData.actions || []) {
          const action = actionWrapper.action;
          if (
            action?.name === "StudentInput" &&
            action.answer_fragment_index !== undefined &&
            action.answer_fragment_index <= targetFragment
          ) {
            actionIdsToReveal.push(action.id);
          }
        }
        return actionIdsToReveal;
      } catch {
        return [];
      }
    },

    // Get all action IDs whose answer_fragment_index is above the target fragment
    // Used when navigating backward to unreveal inputs that shouldn't be revealed yet
    getInputsToUnrevealAboveFragment: (targetFragment) => {
      const state = get();
      const currentSlide = state.getSlideByIndex(state.currentSlideIndex);
      if (!currentSlide?.slide_data) return [];

      try {
        const slideData = JSON.parse(currentSlide.slide_data);
        const actionIdsToUnreveal = [];

        for (const actionWrapper of slideData.actions || []) {
          const action = actionWrapper.action;
          if (
            action?.name === "StudentInput" &&
            action.answer_fragment_index !== undefined &&
            action.answer_fragment_index > targetFragment
          ) {
            actionIdsToUnreveal.push(action.id);
          }
        }
        return actionIdsToUnreveal;
      } catch {
        return [];
      }
    },

    // Get properties for each StudentInput action (is_math, answer_fragment_index, etc.)
    // Returns action_id -> { is_math: boolean, answer_fragment_index?: number }
    getStudentInputPropertiesMap: () => {
      const state = get();
      const currentSlide = state.getSlideByIndex(state.currentSlideIndex);
      if (!currentSlide?.slide_data) return {};

      try {
        const slideData = JSON.parse(currentSlide.slide_data);
        const propsMap = {};

        for (const actionWrapper of slideData.actions || []) {
          const action = actionWrapper.action;
          if (action?.name === "StudentInput") {
            propsMap[action.id] = {
              is_math: action.is_math !== false, // default to true if not specified
              answer_fragment_index: action.answer_fragment_index, // for finding answer element styles
            };
          }
        }
        return propsMap;
      } catch {
        return {};
      }
    },

    setCurrentLessonId: (lessonId) => {
      if (lessonId == null || lessonId === "") {
        set({ currentLessonId: null });
        return;
      }

      const normalizedLessonId =
        typeof lessonId === "number" ? lessonId : Number(lessonId);

      set({
        currentLessonId:
          Number.isFinite(normalizedLessonId) && normalizedLessonId > 0
            ? normalizedLessonId
            : null,
      });
    },

    setCurrentLessonName: (lessonName) => {
      set({ currentLessonName: lessonName });
    },

    setCurrentSlideIndex: (slideIndex) => {
      // Any caller reaching here has a real index — see hasResolvedSlideIndex.
      set({ currentSlideIndex: slideIndex, hasResolvedSlideIndex: true });
    },

    setCurrentDeck: (deck) => {
      set({ currentDeck: deck });
    },

    setCurrentUrl: (url) => {
      set({ currentUrl: url });
    },

    setSlideHtmlCache: (...args) => slideHtmlCacheController.setCache(...args),

    getSlideHtmlFromCache: (...args) =>
      slideHtmlCacheController.getCached(...args),

    setSlideHtmlInFlight: (...args) =>
      slideHtmlCacheController.setInFlight(...args),

    getSlideHtmlInFlight: (...args) =>
      slideHtmlCacheController.getInFlight(...args),

    clearSlideHtmlInFlight: (...args) =>
      slideHtmlCacheController.clearInFlight(...args),

    loadSlideHtml: (options) => slideHtmlCacheController.loadSlideHtml(options),

    prefetchSlideHtml: (options) =>
      slideHtmlCacheController.prefetchSlideHtml(options),

    clearSlideHtmlCache: () => slideHtmlCacheController.clearAll(),

    resetSlideForRestart: (slideIndex) => {
      const targetIndex = Number(slideIndex);
      set((prev) => {
        const nextFragmentMemory = { ...(prev.slideFragmentMemory || {}) };
        if (Number.isInteger(targetIndex)) {
          delete nextFragmentMemory[targetIndex];
        }
        return {
          slideFragmentMemory: nextFragmentMemory,
          slideActionCount: 0,
          ...IDLE_SLIDE_CHANGE_STATE,
          slideResetVersion: (prev.slideResetVersion || 0) + 1,
        };
      });
      const deck = get().currentDeck;
      if (deck && typeof deck.slide === "function") {
        try {
          deck.slide(Number.isInteger(targetIndex) ? targetIndex : 0, 0, 0);
        } catch {
          // Best effort. The reset version above remounts the renderer if direct navigation fails.
        }
      }
    },

    resetAllSlideProgress: () => {
      set((prev) => ({
        slideFragmentMemory: {},
        slideActionCount: 0,
        ...IDLE_SLIDE_CHANGE_STATE,
        slideResetVersion: (prev.slideResetVersion || 0) + 1,
      }));
      const deck = get().currentDeck;
      if (deck && typeof deck.slide === "function") {
        try {
          deck.slide(0, 0, 0);
        } catch {
          // Best effort. The reset version above remounts the renderer if direct navigation fails.
        }
      }
    },

    clearCurrentSlideIndex: () => {
      // No index is, by definition, not a resolved index. Only reached on
      // lesson unmount today, but leaving the flag set would hand the next
      // lesson a stale "resolved" before its own discovery has run.
      set({ currentSlideIndex: null, hasResolvedSlideIndex: false });
    },

    clearSlides: () => {
      set({
        slides: [],
        slideFragmentMemory: {},
        ...createSlideHtmlCacheState(),
      });
    },

    handleChangeSlide: (...args) =>
      slideNavigationController.changeSlide(...args),

    /**
     * Run one student-initiated navigation (Next, Previous, the bite list),
     * refusing to start while another is under way or an auto-advance holds
     * the lock (its countdown, or its change_slide until it answers).
     *
     * Input:   navigate — async; must return handleChangeSlide's promise.
     * Output:  true if it ran, false if either lock was held.
     * Mutates: isManualSlideChangeInFlight.
     *
     * Held from the click, not from the request: the screenshot and notepad
     * checkpoint awaits come first, and pendingSlideChangeRequest is still
     * null while they run. Capped because tutorApi has no timeout — a hung
     * change_slide would otherwise lock navigation for good.
     */
    runManualSlideChange: async (navigate) => {
      // Read here rather than trusting the caller: a control rendered before
      // the countdown started still sees it unlocked.
      if (
        get().isManualSlideChangeInFlight ||
        useNotificationStore.getState().isSlideChangeNotificationActive
      ) {
        return false;
      }
      set({ isManualSlideChangeInFlight: true });
      let lockTimeoutId;
      try {
        await Promise.race([
          navigate(),
          new Promise((resolve) => {
            lockTimeoutId = setTimeout(resolve, SLIDE_CHANGE_LOCK_MAX_MS);
          }),
        ]);
      } finally {
        clearTimeout(lockTimeoutId);
        set({ isManualSlideChangeInFlight: false });
      }
      return true;
    },

    fetchLessons: async () => {
      try {
        const response = await api.get("/lessons");
        set({ lessons: response.data });
      } catch (error) {
        console.error(error);
      }
    },

    fetchSlidesByLesson: async (lessonId, mode = TutorMode.PUBLIC) => {
      const normalizedLessonId =
        typeof lessonId === "number" ? lessonId : Number(lessonId);

      if (!Number.isFinite(normalizedLessonId) || normalizedLessonId <= 0) {
        debugLog("Lesson ID is not ready, skipping slide fetch", { lessonId });
        return;
      }

      const fetchStartedAt = getLessonStartupPerfNow();
      recordLessonStartupPerf("slides_fetch_begin", {
        lesson_id: normalizedLessonId,
        mode,
      });

      try {
        const response = await api.get(
          `/lessons/${normalizedLessonId}/slides?mode=${mode}`,
        );
        set({
          slides: response.data.slides ?? [],
          currentLessonName: response.data.lesson_name,
          slideFragmentMemory: {},
          ...createSlideHtmlCacheState(),
          ...IDLE_SLIDE_CHANGE_STATE,
        });
        recordLessonStartupPerf(
          "slides_fetch_success",
          {
            lesson_id: normalizedLessonId,
            mode,
            slide_count: response.data.slides?.length ?? 0,
          },
          fetchStartedAt,
        );
      } catch (error) {
        console.error("Error fetching slides by lesson:", error);
        recordLessonStartupPerf(
          "slides_fetch_error",
          {
            lesson_id: normalizedLessonId,
            mode,
            message: error instanceof Error ? error.message : String(error),
          },
          fetchStartedAt,
        );
      }
    },
  };
};

export const useSlideStore = createStore("SlideStore", base);
